/**
 * The browser entry point: the real ports, handed to `startApp`. Nothing else
 * belongs here — it is the one file no unit test can load (it touches
 * `document` at import), listed in dev/coverage/neverLoaded.ts.
 */
import './styles/globals.css';
import { toast } from 'sonner';
import { startApp } from './app/start.js';
import { realSrpLib } from './auth/cognito.js';
import type { StoragePort } from './auth/session.js';
import { registerServiceWorker } from './sw/register.js';

/** localStorage, guarded: absent or throwing (private mode, blocked site data) reads as empty and ignores writes. */
const storage: StoragePort = {
  get: (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Storage unavailable: the session lasts as long as the tab.
    }
  },
  remove: (key) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // As above.
    }
  },
};

const container = document.getElementById('root');
if (container === null) throw new Error('index.html has no #root element');

void startApp({
  container,
  origin: window.location.origin,
  // A `timeoutMs` (the health probes') aborts the request, so a dead host leaves no hung sockets behind.
  fetch: (url, { timeoutMs, ...init }) => window.fetch(url, timeoutMs === undefined ? init : { ...init, signal: AbortSignal.timeout(timeoutMs) }),
  fetchJson: (path) => window.fetch(path, { headers: { accept: 'application/json' }, cache: 'no-store' }),
  storage,
  onStorageChange: (listener) => window.addEventListener('storage', listener),
  nowMs: () => Date.now(),
  after: (ms, fn) => {
    const id = window.setTimeout(fn, ms);
    return () => window.clearTimeout(id);
  },
  srpLib: realSrpLib,
  root: document.documentElement,
  // Chrome's Local Network Access permission (connectivity/localNetwork.ts); absent in browsers without the API.
  permissions: 'permissions' in navigator ? navigator.permissions : null,
  // D35: a field a newer server added is dropped, not refused — this build may be older than the API it talks to.
  responses: 'tolerant',
});

// The app shell's service worker (src/sw/): production builds only — under `vite dev` it would cache modules HMR
// replaces. It keeps the UI loadable while a node restarts; a new build waits for the operator's "Reload".
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  void registerServiceWorker({
    container: navigator.serviceWorker,
    onUpdateReady: (apply) => {
      toast('A new version of ACP is ready', { duration: Number.POSITIVE_INFINITY, action: { label: 'Reload', onClick: apply } });
    },
    reload: () => window.location.reload(),
    every: (ms, fn) => {
      window.setInterval(fn, ms);
    },
    reportError: (error) => {
      console.warn('[acp] service worker:', error);
    },
  });
}
